/* eslint-disable react/display-name */
// react
import React, { useEffect, useState, useRef } from 'react'
import dayjs from 'dayjs'
import { CSVLink } from 'react-csv'

import { Button, BUTTON_THEME, Icon, IconButton } from '@st-operations/uikit/src/v2/index.js'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'
import Events from '@st-operations/uikit/Events.js'
import pretty from '@st-operations/uikit/lib/pretty'

// components
import ListView from '_components/list-view/index.jsx'
import Header from '_components/header/index.jsx'
import MediaMeasurementCampaignsStatusBadge from '_components/media-measurement-campaign-status-badge'
import BatchSidebar, { ACTIONS, clearBatchSidebar } from '_components/batch-sidebar'

import CreateMediaMeasurementCampaignModal from '_components/online-media-measurements/create-media-measurement-campaign-modal'
import ModalCloneAudience from '_components/online-media-measurements/modal-clone-audience'
import AccountingModal from '_components/online-media-measurements/accounting-modal'
import {
  OMM_STATUS_OPTIONS,
  OMM_REPORTS_STATUS_OPTIONS,
  SCHEMA,
} from '_components/online-media-measurements/constants'

import {
  createMediaMeasurementCampaign,
  deleteMediaMeasurementCampaigns,
} from '_services/campaigns'
import { getOrgs } from '_services/permissions'
import { getEnumCreatedBy } from '_services/campaigns'
import { newLog } from '_services/logs'

// Styles
import './styles.scss'

const CustomCard = ({ item, handleRemove }) => {
  const { name, status, created_by } = item
  return (
    <div className="batch-card">
      <header className="batch-card__header">
        <p>{name}</p>
        <IconButton
          onClick={handleRemove}
          aria-label="Remove Item"
          iconProps={{
            icon: 'delete',
            type: 'fill',
            width: 16,
            height: 16,
            color: '#13682B',
          }}
        />
      </header>
      <div className="batch-card__meta">
        <div>
          <Icon icon="info" type="outline" color="#666666" width="16px" height="16px" />
          <span>{status}</span>
        </div>
        <div style={{ gridArea: 'user' }}>
          <Icon icon="contacts" type="outline" color="#666666" width="16px" height="16px" />
          <span>{created_by.email}</span>
        </div>
      </div>
    </div>
  )
}

const MediaMeasurementCampaigns = () => {
  const [newAudienceModal, setNewAudienceModal] = useState(false)
  const [accountingModal, setAccountingModal] = useState(false)
  const [createdByOptions, setCreatedByOptions] = useState([])
  const [orgOptions, setOrgOptions] = useState([])

  const [cloneAudiences, setCloneAudiences] = useState([])
  const listViewItemsRef = useRef([])

  const [refreshKey, setRefreshKey] = useState(0)
  const refresh = () => setRefreshKey(Math.random())

  const [csvData, setCsvData] = useState(null)
  const csvLinkRef = useRef(null)

  useEffect(() => {
    getOrgs().then(({ orgs }) => {
      const options = [
        { label: 'Except ShareThis', value: orgs.filter((org) => org !== 'sharethis').join(',') },
        ...orgs.map((org) => ({ label: pretty(org), value: org })),
      ]
      setOrgOptions(options)
    })
    getEnumCreatedBy().then(setCreatedByOptions)
  }, [])

  const handleCloneModal = (audiences) => {
    const formattedAudiences = Object.values(audiences).map((audience) => {
      const data = Object.fromEntries(
        Object.keys(SCHEMA).map((key) => [key.toLowerCase(), audience[key.toLowerCase()]])
      )
      data.status = OMM_STATUS_OPTIONS.DRAFT
      return data
    })

    const requests = formattedAudiences.map((audience) => createMediaMeasurementCampaign(audience))
    Promise.all(requests)
      .then((items) => {
        items.forEach((item) =>
          newLog({
            entity: 'media-measurement-campaigns',
            data: {
              action: 'create draft',
              audienceName: item.name,
              audienceId: item._id,
            },
          })
        )
        toast.success('Audiences Cloned!')
        clearBatchSidebar()
        refresh()
      })
      .catch((error) => toast.error(error.message))
  }

  const deleteAudiences = (audiences) => {
    const filteredDeliveredExternalAudiences = window._isShareThisUser
      ? audiences
      : audiences.filter(
          (audience) =>
            ![
              OMM_STATUS_OPTIONS.SUBMITTED_FOR_DELIVERY,
              OMM_STATUS_OPTIONS.DELIVERING,
              OMM_STATUS_OPTIONS.DELIVERED,
            ].includes(audience.status)
        )
    const requests = filteredDeliveredExternalAudiences.map((audience) =>
      deleteMediaMeasurementCampaigns(audience._id)
    )
    Promise.all(requests)
      .then((items) => {
        items.forEach((item, index) => {
          if (item.status === 'deleted') {
            newLog({
              entity: 'media-measurement-campaigns',
              data: {
                action: 'delete',
                audienceName: audiences[index].name,
                audienceId: audiences[index]._id,
              },
            })
          }
        })
        toast.success('Audiences Deleted!')
        clearBatchSidebar()
        refresh()
      })
      .catch((error) => toast.error(error.message))
  }

  const exportAudiences = (audiences) => {
    const data = audiences.map(({ name, created_at, status, created_by, deliveries }) => ({
      name,
      status,
      created_by: created_by.email,
      created_at,
      delivery_date: deliveries?.[0]?.delivery_date ?? '-',
      ...(window._isShareThisUser ? { organization: created_by.org } : {}),
    }))
    setCsvData(data)

    // triggers download after state is updated
    setTimeout(() => {
      try {
        csvLinkRef.current.link.click()
        setCsvData(null)
        clearBatchSidebar()
        toast('Audiences exported with success')
      } catch (error) {
        toast.error('An error has occurred')
      }
    }, 0)
  }

  const handleSelectionChange = ({ action, items }) => {
    const selectedEntities = listViewItemsRef.current.filter(({ _id }) => items.includes(_id))
    Events.emit('ListView-selection', { action, items, entities: selectedEntities })
  }

  const handleBatchAction = (action, items) => {
    switch (action) {
      case ACTIONS.CLONE:
        setCloneAudiences(items)
        break
      case ACTIONS.DELETE:
        deleteAudiences(items)
        break
      case ACTIONS.EXPORT:
        exportAudiences(items)
        break
    }
  }

  const handleCloseCloneModal = () => setCloneAudiences([])

  return (
    <div id="media-measurement-campaigns-page" className="media-measurement-campaigns">
      <Header title="Online Media Measurements">
        <If condition={window._isShareThisUser}>
          <Button theme={BUTTON_THEME.SECONDARY} onClick={() => setAccountingModal(Math.random())}>
            Accounting Report
          </Button>
        </If>
        <Button theme={BUTTON_THEME.PRIMARY} onClick={() => setNewAudienceModal(true)}>
          Create New Audience
        </Button>
      </Header>
      <div className="batch-sidebar__list-view-wrapper">
        <ListView
          key={refreshKey}
          itemsRef={listViewItemsRef}
          onSelectionChange={handleSelectionChange}
          columns={[
            {
              name: 'Name',
              link: '/online-media-measurements/:_id',
              key: 'name',
              styles: {
                maxWidth: '400px',
                textAlign: 'left',
              },
              sortable: true,
            },
            {
              name: 'Status',
              key: 'status',
              styles: {
                maxWidth: '200px',
                textAlign: 'left',
              },
              render: (item) => <MediaMeasurementCampaignsStatusBadge status={item.status} />,
              sortable: true,
            },
            {
              name: 'Reports Status',
              key: 'reports_status',
              styles: {
                maxWidth: '200px',
                textAlign: 'left',
              },
              render: (item) => (
                <MediaMeasurementCampaignsStatusBadge status={item.reports_status} />
              ),
              sortable: true,
            },
            {
              name: 'Delivery Date',
              // key: 'deliveries[0].delivery_date',
              styles: {
                maxWidth: '200px',
                textAlign: 'center',
              },
              render: (item) => (
                <span>
                  {item.deliveries?.[0]?.delivery_date
                    ? dayjs(item.deliveries?.[0]?.delivery_date).format('MM/DD/YYYY')
                    : '-'}
                </span>
              ),
              sortable: true,
            },
            {
              name: 'Created At',
              key: 'created_at',
              styles: {
                maxWidth: '200px',
                textAlign: 'left',
              },
              render: (item) => <span>{dayjs(item.created_at).format('MM/DD/YYYY HH:mm')}</span>,
              sortable: true,
            },
            {
              name: 'Created By',
              key: 'created_by.email',
              styles: {
                maxWidth: '200px',
                textAlign: 'left',
              },
            },
            window._isShareThisUser
              ? {
                  name: 'Organization',
                  key: 'created_by.org',
                  styles: {
                    maxWidth: '200px',
                    textAlign: 'left',
                  },
                }
              : null,
          ].filter(Boolean)}
          entities="media-measurement-campaigns"
          searchable
          showCheckboxes={true}
          filters={[
            {
              name: 'status',
              label: 'Status',
              options: Object.values(OMM_STATUS_OPTIONS),
              all: true,
            },
            {
              name: 'reports_status',
              label: 'Reports Status',
              options: Object.values(OMM_REPORTS_STATUS_OPTIONS),
              all: true,
            },
            {
              name: 'created_by.email',
              label: 'Created by',
              options: createdByOptions,
              all: true,
              className: 'audiences__filters',
            },
            window._isShareThisUser
              ? {
                  name: 'created_by.org',
                  label: 'Organization',
                  options: orgOptions,
                  all: true,
                  className: 'audiences__filters',
                }
              : null,
          ].filter(Boolean)}
          sort_key={'updated_at'}
          sort_dir={-1}
        />
        <BatchSidebar
          onConfirm={handleBatchAction}
          actions={[ACTIONS.CLONE, ACTIONS.DELETE, ACTIONS.EXPORT]}
          CustomCard={CustomCard}
        />
      </div>
      <Dialog open={newAudienceModal} onClose={() => setNewAudienceModal(false)}>
        <CreateMediaMeasurementCampaignModal
          isOpen={newAudienceModal}
          onClose={() => setNewAudienceModal(false)}
          refresh={refresh}
        />
      </Dialog>
      <Dialog open={accountingModal} onClose={() => setAccountingModal(false)}>
        <AccountingModal key={accountingModal} isOpen={accountingModal} />
      </Dialog>

      <If condition={csvData}>
        <CSVLink
          ref={csvLinkRef}
          data={csvData}
          filename={`Audience Report - ${dayjs(new Date()).format('YYYY-MM-DD')}.csv`}
        >
          Download Report
        </CSVLink>
      </If>

      <ModalCloneAudience
        selectedAudiences={cloneAudiences}
        isModalCloneOpen={cloneAudiences.length}
        clone={handleCloneModal}
        closeModal={handleCloseCloneModal}
      />
    </div>
  )
}

// export
export default MediaMeasurementCampaigns
